import { useEffect, useState, type ClipboardEvent, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';

import type { S3ConfigInput, S3ConfigViewModel } from '../api';

interface Draft {
  name: string;
  bucketName: string;
  region: string;
  accessKeyId: string;
  secretAccessKey: string;
  endpointUrl: string;
  folder: string;
  isDefault: boolean;
  active: boolean;
}

const EMPTY_DRAFT: Draft = {
  name: '',
  bucketName: '',
  region: '',
  accessKeyId: '',
  secretAccessKey: '',
  endpointUrl: '',
  folder: '',
  isDefault: false,
  active: true,
};

function draftFrom(row: S3ConfigViewModel): Draft {
  return {
    name: row.name,
    bucketName: row.bucketName,
    region: row.region,
    accessKeyId: row.accessKeyId,
    secretAccessKey: '',
    endpointUrl: row.endpointUrl,
    folder: row.folder,
    isDefault: row.isDefault,
    active: row.active,
  };
}

/**
 * Maps each editable draft field to its key in the clipboard JSON (snake_case — the same shape the
 * API uses, so an exported config reads like the wire DTO). Read-only fields (id, has_secret) are
 * intentionally excluded.
 */
type ClipboardKind = 'string' | 'boolean';
interface ClipboardField {
  readonly json: string;
  readonly draft: keyof Draft;
  readonly kind: ClipboardKind;
}

const CLIPBOARD_FIELDS: readonly ClipboardField[] = [
  { json: 'name', draft: 'name', kind: 'string' },
  { json: 'bucket_name', draft: 'bucketName', kind: 'string' },
  { json: 'region', draft: 'region', kind: 'string' },
  { json: 'access_key_id', draft: 'accessKeyId', kind: 'string' },
  { json: 'secret_access_key', draft: 'secretAccessKey', kind: 'string' },
  { json: 'endpoint_url', draft: 'endpointUrl', kind: 'string' },
  { json: 'folder', draft: 'folder', kind: 'string' },
  { json: 'is_default', draft: 'isDefault', kind: 'boolean' },
  { json: 'active', draft: 'active', kind: 'boolean' },
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Serialise the editable draft fields to a plain JSON object (snake_case). */
function draftToJson(draft: Draft): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const field of CLIPBOARD_FIELDS) {
    out[field.json] = draft[field.draft];
  }
  return out;
}

/** Apply a parsed clipboard object onto the draft. Accepts both snake_case and the draft key. */
function applyJsonToDraft(prev: Draft, parsed: Record<string, unknown>): { draft: Draft; applied: number } {
  const next = { ...prev } as Record<keyof Draft, string | boolean>;
  let applied = 0;
  for (const field of CLIPBOARD_FIELDS) {
    const raw = field.json in parsed ? parsed[field.json] : parsed[field.draft];
    if (raw === undefined) continue;
    if (field.kind === 'boolean') {
      if (typeof raw === 'boolean') {
        next[field.draft] = raw;
        applied += 1;
      }
    } else if (typeof raw === 'string') {
      next[field.draft] = raw;
      applied += 1;
    }
  }
  return { draft: next as unknown as Draft, applied };
}

interface FieldErrors {
  name?: string;
  bucketName?: string;
  region?: string;
  accessKeyId?: string;
  secretAccessKey?: string;
}

type ClipboardNote = { readonly kind: 'ok' | 'error'; readonly text: string } | null;

export function S3ConfigFormDialog({
  open,
  initial,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  /** Non-null = edit; null = create. */
  readonly initial: S3ConfigViewModel | null;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: S3ConfigInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [copied, setCopied] = useState(false);
  const [clipboardNote, setClipboardNote] = useState<ClipboardNote>(null);
  const isEdit = initial !== null;

  useEffect(() => {
    if (open) {
      setDraft(initial !== null ? draftFrom(initial) : EMPTY_DRAFT);
      setErrors({});
      setCopied(false);
      setClipboardNote(null);
    }
  }, [open, initial]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((p) => ({ ...p, [key]: value }));
  }

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(JSON.stringify(draftToJson(draft), null, 2));
      setCopied(true);
      setClipboardNote(null);
      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      setClipboardNote({ kind: 'error', text: t('routes.admin.s3.configs.form.clipboard.copyError') });
    }
  }

  /**
   * Import on a real paste gesture (Ctrl/Cmd+V) — the paste event carries the clipboard text
   * directly, so there's no browser permission prompt. We only hijack the paste when it parses to
   * one of our exported configs; otherwise it falls through as a normal paste into the focused field.
   */
  function handlePaste(event: ClipboardEvent<HTMLFormElement>): void {
    const text = event.clipboardData.getData('text');
    if (text.trim().length === 0) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return;
    }
    if (!isRecord(parsed)) return;
    const { draft: nextDraft, applied } = applyJsonToDraft(draft, parsed);
    if (applied === 0) return;
    event.preventDefault();
    setDraft(nextDraft);
    setErrors({});
    setClipboardNote({
      kind: 'ok',
      text: t('routes.admin.s3.configs.form.clipboard.imported', { count: applied }),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: FieldErrors = {};
    if (draft.name.trim().length === 0) next.name = t('routes.admin.s3.configs.form.errors.nameRequired');
    if (draft.bucketName.trim().length === 0)
      next.bucketName = t('routes.admin.s3.configs.form.errors.bucketNameRequired');
    if (draft.region.trim().length === 0) next.region = t('routes.admin.s3.configs.form.errors.regionRequired');
    if (draft.accessKeyId.trim().length === 0)
      next.accessKeyId = t('routes.admin.s3.configs.form.errors.accessKeyIdRequired');
    const secretRequired = !(initial?.hasSecret ?? false);
    if (secretRequired && draft.secretAccessKey.trim().length === 0)
      next.secretAccessKey = t('routes.admin.s3.configs.form.errors.secretRequired');
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      name: draft.name,
      bucketName: draft.bucketName,
      region: draft.region,
      accessKeyId: draft.accessKeyId,
      secretAccessKey: draft.secretAccessKey,
      endpointUrl: draft.endpointUrl,
      folder: draft.folder,
      isDefault: draft.isDefault,
      active: draft.active,
    });
  }

  const fieldPrefix = 'routes.admin.s3.configs.form.fields';
  const hintPrefix = 'routes.admin.s3.configs.form.hints';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('routes.admin.s3.configs.edit.title')
              : t('routes.admin.s3.configs.create.title')}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          onPaste={handlePaste}
          noValidate
          className="space-y-4 max-h-[70vh] overflow-y-auto pr-1"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField
              id="s3-config-name"
              label={t(`${fieldPrefix}.name`)}
              hint={t(`${hintPrefix}.name`)}
              required
              error={errors.name}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.name}
                  onChange={(e) => {
                    set('name', e.target.value);
                  }}
                  autoComplete="off"
                />
              )}
            </FormField>
            <FormField
              id="s3-config-bucket-name"
              label={t(`${fieldPrefix}.bucketName`)}
              hint={t(`${hintPrefix}.bucketName`)}
              required
              error={errors.bucketName}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.bucketName}
                  onChange={(e) => {
                    set('bucketName', e.target.value);
                  }}
                  autoComplete="off"
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <FormField
              id="s3-config-region"
              label={t(`${fieldPrefix}.region`)}
              hint={t(`${hintPrefix}.region`)}
              required
              error={errors.region}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.region}
                  onChange={(e) => {
                    set('region', e.target.value);
                  }}
                  placeholder="us-east-1"
                  autoComplete="off"
                  className="font-mono text-sm"
                />
              )}
            </FormField>

            <FormField
              id="s3-config-access-key-id"
              label={t(`${fieldPrefix}.accessKeyId`)}
              hint={t(`${hintPrefix}.accessKeyId`)}
              required
              error={errors.accessKeyId}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.accessKeyId}
                  onChange={(e) => {
                    set('accessKeyId', e.target.value);
                  }}
                  autoComplete="off"
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <FormField
              id="s3-config-secret-access-key"
              label={t(`${fieldPrefix}.secretAccessKey`)}
              hint={t(`${hintPrefix}.secretAccessKey`)}
              error={errors.secretAccessKey}
            >
              {(fp) => (
                <Input
                  {...fp}
                  type="password"
                  value={draft.secretAccessKey}
                  onChange={(e) => {
                    set('secretAccessKey', e.target.value);
                  }}
                  autoComplete="off"
                  placeholder={
                    (initial?.hasSecret ?? false)
                      ? t('routes.admin.s3.configs.form.secretKeepPlaceholder')
                      : undefined
                  }
                />
              )}
            </FormField>

            <FormField
              id="s3-config-endpoint-url"
              label={t(`${fieldPrefix}.endpointUrl`)}
              hint={t(`${hintPrefix}.endpointUrl`)}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.endpointUrl}
                  onChange={(e) => {
                    set('endpointUrl', e.target.value);
                  }}
                  placeholder="https://s3.example.com"
                  autoComplete="off"
                  className="font-mono text-xs"
                />
              )}
            </FormField>

            <FormField
              id="s3-config-folder"
              label={t(`${fieldPrefix}.folder`)}
              hint={t(`${hintPrefix}.folder`)}
              className="sm:col-span-3"
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.folder}
                  onChange={(e) => {
                    set('folder', e.target.value);
                  }}
                  autoComplete="off"
                  className="font-mono text-sm"
                />
              )}
            </FormField>
          </div>

          <div className="flex items-center gap-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isDefault}
                onChange={(e) => {
                  set('isDefault', e.target.checked);
                }}
                className="h-3.5 w-3.5"
              />
              {t(`${fieldPrefix}.isDefault`)}
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => {
                  set('active', e.target.checked);
                }}
                className="h-3.5 w-3.5"
              />
              {t(`${fieldPrefix}.active`)}
            </label>
          </div>

          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.admin.s3.configs.form.errors.submit', { code: getErrorCodeForUi(submitError) })}
            </p>
          )}

          <DialogFooter className="sm:items-center sm:justify-between sm:space-x-0">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void handleCopy();
                }}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied
                  ? t('routes.admin.s3.configs.form.clipboard.copied')
                  : t('routes.admin.s3.configs.form.clipboard.copy')}
              </Button>
              {clipboardNote !== null ? (
                <span
                  role={clipboardNote.kind === 'error' ? 'alert' : undefined}
                  className={
                    clipboardNote.kind === 'error' ? 'text-xs text-red-600' : 'text-xs text-muted-foreground'
                  }
                >
                  {clipboardNote.text}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {t('routes.admin.s3.configs.form.clipboard.pasteHint')}
                </span>
              )}
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                }}
                disabled={pending}
              >
                {t('routes.admin.s3.configs.form.cancel')}
              </Button>
              <Button type="submit" loading={pending}>
                {isEdit
                  ? t('routes.admin.s3.configs.edit.submit')
                  : t('routes.admin.s3.configs.create.submit')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
